import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, CheckCircle2, Circle, FileText, RotateCcw, Calendar, MessageSquare, X, Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import api, { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STATUS_FILTERS = ["all", "done", "todo"];

export default function ConnexionPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const uiLocale = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const [data, setData] = useState({ ateliers: [], total_in_catalog: 0, groupe_id: null });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);          // {atelier_id, fait, date_seance, note_coach}
  const [previewing, setPreviewing] = useState(null);    // {id, title, lang, url}

  const refresh = async (gid) => {
    setLoading(true);
    try {
      const params = gid ? { groupe_id: gid } : {};
      const { data } = await api.get("/connexion/ateliers", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load connexion ateliers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);
  // Re-fetch when user picks another group from the selector
  const switchGroup = (gid) => { refresh(gid); };

  const doneCount = useMemo(
    () => data.ateliers.filter((a) => a.progress?.fait).length,
    [data.ateliers]
  );

  const filtered = useMemo(() => {
    return data.ateliers.filter((a) => {
      if (statusFilter === "done" && !a.progress?.fait) return false;
      if (statusFilter === "todo" && a.progress?.fait) return false;
      if (query) {
        const q = query.toLowerCase();
        return (
          a.id.toLowerCase().includes(q) ||
          (a.title_fr || "").toLowerCase().includes(q) ||
          (a.title_en || "").toLowerCase().includes(q) ||
          String(a.semaine || "").includes(q)
        );
      }
      return true;
    });
  }, [data.ateliers, query, statusFilter]);

  const handleSave = async (atelierId, payload) => {
    try {
      await api.post(`/connexion/ateliers/${atelierId}/progress`, payload);
      toast.success(t("connexion.saved_success"));
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("connexion.save_error"));
    }
  };

  const handleReset = async (atelierId) => {
    try {
      await api.delete(`/connexion/ateliers/${atelierId}/progress`);
      toast.success(t("connexion.reset_success"));
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("connexion.save_error"));
    }
  };

  const openPreview = async (atelier) => {
    const lang = uiLocale;
    const link = atelier[`gdrive_script_${lang}`];
    if (!link) {
      toast.error(t("connexion.no_pdf"));
      return;
    }
    // Stream the PDF through our authenticated endpoint into a blob URL
    try {
      const token = localStorage.getItem("delphi_token");
      const res = await fetch(
        `${API_BASE}/connexion/ateliers/${atelier.id}/file?lang=${lang}&inline=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewing({
        id: atelier.id,
        title: atelier[`title_${lang}`] || atelier.id,
        lang,
        url,
      });
    } catch (e) {
      toast.error(e.message || t("connexion.no_pdf"));
    }
  };

  const closePreview = () => {
    if (previewing?.url) URL.revokeObjectURL(previewing.url);
    setPreviewing(null);
  };

  const hasPlaceholders = data.ateliers.some((a) => a.is_placeholder);
  const canWrite = Boolean(data.can_write);
  const isReadOnly = !canWrite;

  return (
    <div className="space-y-6" data-testid="connexion-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2 flex items-center gap-2">
          <Lock className="w-3 h-3" />
          {t("connexion.header_kicker")} · {t("connexion.header_access")}
        </div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">
          {t("connexion.header_title")}
        </h1>
        <p className="font-oracle italic text-lg text-text-secondary mt-2" data-testid="connexion-subtitle">
          {t("connexion.header_subtitle", { done: doneCount, total: data.total_in_catalog })}
        </p>

        {hasPlaceholders && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="placeholder-warning">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{t("connexion.placeholder_warning")}</span>
          </div>
        )}

        {isReadOnly && data.groupe_id && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-sm" data-testid="readonly-banner">
            <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{t("connexion.readonly_supervisor")}</span>
          </div>
        )}

        {/* Group selector for DN/CEO (multi-group supervisor view) */}
        {data.available_groups && data.available_groups.length > 1 && (
          <div className="mt-4 flex items-center gap-2" data-testid="group-selector-wrapper">
            <label htmlFor="connexion-group-select" className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              {t("connexion.viewing_group")}
            </label>
            <select
              id="connexion-group-select"
              data-testid="group-selector"
              value={data.groupe_id || ""}
              onChange={(e) => switchGroup(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-apollo-orange/40"
            >
              {data.available_groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.nom}{g.ville ? ` · ${g.ville}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {!data.groupe_id && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-900 text-sm" data-testid="no-group-warning">
            {t("connexion.no_group")}
          </div>
        )}
      </header>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
          <Input
            type="text"
            placeholder={t("connexion.search_placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 h-11"
            data-testid="connexion-search"
          />
        </div>
        <div className="flex gap-2" data-testid="connexion-status-filters">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-status-${s}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                statusFilter === s
                  ? "bg-apollo-orange text-white"
                  : "bg-white border border-border text-text-secondary hover:border-apollo-orange/40"
              }`}
            >
              {t(`connexion.filter_${s}`)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-32 bg-bg-soft rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-text-secondary" data-testid="connexion-empty">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{t("connexion.empty")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="connexion-grid">
          {filtered.map((a) => (
            <ConnexionCard
              key={a.id}
              atelier={a}
              locale={uiLocale}
              t={t}
              onMark={() => setEditing({
                atelier_id: a.id,
                fait: true,
                date_seance: a.progress?.date_seance || new Date().toISOString().slice(0, 10),
                note_coach: a.progress?.note_coach || "",
                title: a[`title_${uiLocale}`],
              })}
              onPreview={() => openPreview(a)}
              canMark={Boolean(data.groupe_id) && canWrite}
              readOnly={isReadOnly}
            />
          ))}
        </div>
      )}

      {editing && (
        <EditDialog
          editing={editing}
          t={t}
          onClose={() => setEditing(null)}
          onSave={() => handleSave(editing.atelier_id, {
            fait: editing.fait,
            date_seance: editing.date_seance,
            note_coach: editing.note_coach,
          })}
          onReset={() => handleReset(editing.atelier_id)}
          setEditing={setEditing}
          isCurrentlyDone={data.ateliers.find((a) => a.id === editing.atelier_id)?.progress?.fait}
        />
      )}

      {previewing && (
        <PreviewDialog previewing={previewing} onClose={closePreview} />
      )}
    </div>
  );
}

function ConnexionCard({ atelier, locale, t, onMark, onPreview, canMark, readOnly }) {
  const title = atelier[`title_${locale}`] || atelier.id;
  const pdfAvailable = Boolean(atelier[`gdrive_script_${locale}`]);
  const done = atelier.progress?.fait;
  return (
    <article
      data-testid={`connexion-card-${atelier.id}`}
      className={`rounded-xl border p-4 transition-colors ${
        done ? "bg-green-50 border-green-200" : "bg-white border-border hover:border-apollo-orange/40"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono font-bold text-apollo-orange-dark">{atelier.id}</span>
          {atelier.semaine ? (
            <span className="text-text-secondary">· {t("connexion.week_label", { n: atelier.semaine })}</span>
          ) : null}
        </div>
        {done ? (
          <span className="flex items-center gap-1 text-xs font-bold text-green-700">
            <CheckCircle2 className="w-4 h-4" /> {t("connexion.status_done")}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-text-secondary">
            <Circle className="w-4 h-4" /> {t("connexion.status_todo")}
          </span>
        )}
      </div>

      <h3 className="font-semibold text-text-primary leading-tight mb-3" data-testid={`title-${atelier.id}`}>
        {title}
      </h3>

      {done && (atelier.progress.date_seance || atelier.progress.note_coach) && (
        <div className="mb-3 space-y-1 text-xs text-green-900">
          {atelier.progress.date_seance && (
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              {t("connexion.session_date", { date: atelier.progress.date_seance })}
            </div>
          )}
          {atelier.progress.note_coach && (
            <div className="flex items-start gap-1.5">
              <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{atelier.progress.note_coach}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className={`text-xs ${readOnly ? "flex-1" : "flex-1"}`}
          onClick={onPreview}
          disabled={!pdfAvailable}
          data-testid={`btn-preview-${atelier.id}`}
        >
          <FileText className="w-3 h-3 mr-1" />
          {pdfAvailable ? t("connexion.view_pdf") : t("connexion.no_pdf")}
        </Button>
        {!readOnly && (
          <Button
            size="sm"
            variant={done ? "secondary" : "default"}
            className={`flex-1 text-xs ${done ? "" : "bg-apollo-orange hover:bg-apollo-orange-dark text-white"}`}
            onClick={onMark}
            disabled={!canMark}
            data-testid={`btn-mark-${atelier.id}`}
          >
            {done ? <RotateCcw className="w-3 h-3 mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            {done ? t("connexion.mark_undone") : t("connexion.mark_done")}
          </Button>
        )}
      </div>
    </article>
  );
}

function EditDialog({ editing, t, onClose, onSave, onReset, setEditing, isCurrentlyDone }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      onClick={onClose}
      data-testid="connexion-edit-dialog"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-apollo-orange-dark font-bold">
              {editing.atelier_id}
            </div>
            <h3 className="font-heading font-bold text-xl mt-1">{editing.title}</h3>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary" data-testid="close-dialog">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4 mt-4">
          <div>
            <label className="text-xs font-semibold text-text-secondary block mb-1">
              {t("connexion.form_date_label")}
            </label>
            <Input
              type="date"
              value={editing.date_seance || ""}
              onChange={(e) => setEditing({ ...editing, date_seance: e.target.value })}
              data-testid="input-date-seance"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary block mb-1">
              {t("connexion.form_note_label")}
            </label>
            <textarea
              value={editing.note_coach || ""}
              onChange={(e) => setEditing({ ...editing, note_coach: e.target.value })}
              placeholder={t("connexion.form_note_placeholder")}
              rows={4}
              className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-apollo-orange/40"
              data-testid="input-note-coach"
            />
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-between">
          {isCurrentlyDone ? (
            <Button variant="ghost" size="sm" onClick={onReset} data-testid="btn-reset-progress" className="text-red-700 hover:bg-red-50">
              <RotateCcw className="w-3 h-3 mr-1" />
              {t("connexion.form_reset")}
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="btn-cancel-dialog">
              {t("connexion.form_cancel")}
            </Button>
            <Button size="sm" onClick={onSave} className="bg-apollo-orange hover:bg-apollo-orange-dark text-white" data-testid="btn-save-progress">
              {t("connexion.form_save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewDialog({ previewing, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex flex-col"
      onClick={onClose}
      data-testid="connexion-preview-dialog"
    >
      <div
        className="bg-white m-2 md:m-8 flex-1 rounded-xl shadow-xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-border">
          <h3 className="font-semibold text-text-primary truncate">{previewing.title}</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary" data-testid="close-preview">
            <X className="w-5 h-5" />
          </button>
        </div>
        <iframe
          src={previewing.url}
          title={previewing.title}
          className="flex-1 w-full"
          data-testid="connexion-pdf-frame"
        />
      </div>
    </div>
  );
}
